import {
  findExclusionGroupConflicts,
  type ExclusionMember,
} from '@/features/quotes/utils/exclusion-group.util';
import { normalizeCoverageCode } from './coverage-limits.util';
import { COVERAGE_RELATION_DEPENDENT_ON } from './coverage-relations.util';
import type { StepCoveragesFormValues } from './step-coverages.schema';
import type {
  CapitalGlobalAssistanceCatalogItem,
  CapitalGlobalCoverageCatalogItem,
  CapitalGlobalSelectOption,
} from './coverages-catalog';

/**
 * Onde o conflito de exclusão aparece na tela de coberturas. O grupo atravessa
 * tipos, então o mesmo conflito costuma se manifestar em DOIS destinos ao mesmo
 * tempo (ex.: a cobertura Auxílio Cesta Básica e o toggle da Assistência Cesta
 * Básica), e cada um precisa mostrar a mensagem para o corretor entender o que
 * desligar.
 */
export type ExclusionConflictTarget =
  | { kind: 'coverage'; coverageId: string; message: string }
  | { kind: 'assistance'; code: string; message: string }
  | { kind: 'assistanceFuneral'; message: string }
  | { kind: 'capitalization'; message: string };

/* eslint-disable no-unused-vars -- nomes de parâmetro documentam a aridade */
type ExclusionConflictRouter = {
  coverage: (coverageId: string, message: string) => void;
  assistance: (code: string, message: string) => void;
  assistanceFuneral: (message: string) => void;
  capitalization: (message: string) => void;
};
/* eslint-enable no-unused-vars */

/**
 * Entrega o conflito ao destino de tela certo. Exhaustivo no `kind` — se um
 * novo destino for adicionado ao union, o compilador cobra o handler aqui.
 */
export function routeExclusionConflict(
  conflict: ExclusionConflictTarget,
  router: ExclusionConflictRouter
): void {
  switch (conflict.kind) {
    case 'coverage':
      router.coverage(conflict.coverageId, conflict.message);
      return;
    case 'assistance':
      router.assistance(conflict.code, conflict.message);
      return;
    case 'assistanceFuneral':
      router.assistanceFuneral(conflict.message);
      return;
    case 'capitalization':
      router.capitalization(conflict.message);
      return;
  }
}

/** Referência opaca que o núcleo carrega e devolvemos roteada. */
type CapitalGlobalExclusionRef =
  | { kind: 'coverage'; coverageId: string }
  | { kind: 'assistance'; code: string }
  | { kind: 'assistanceFuneral' }
  | { kind: 'capitalization' };

type CapitalGlobalCatalogs = {
  coverageCatalog: CapitalGlobalCoverageCatalogItem[];
  assistanceCatalog: CapitalGlobalAssistanceCatalogItem[];
  assistanceFuneralOptions: CapitalGlobalSelectOption[];
  capitalizationOptions: CapitalGlobalSelectOption[];
};

/**
 * Monta a lista de membros a partir dos quatro pontos onde uma seleção com
 * `ExclusionGroupCode` pode viver no formulário do Capital Global:
 *  - coberturas (toggles, keyed por id);
 *  - assistências standalone (toggles, keyed por code);
 *  - o select de Assistência Funeral (valor único);
 *  - o select de Capitalização (valor único).
 */
function buildMembers(
  values: StepCoveragesFormValues,
  catalogs: CapitalGlobalCatalogs
): ExclusionMember<CapitalGlobalExclusionRef>[] {
  const members: ExclusionMember<CapitalGlobalExclusionRef>[] = [];

  for (const coverage of catalogs.coverageCatalog) {
    // Dependência declarada (`RelationType=1`) vence a exclusão para o par
    // pai↔filho: o catálogo agrupa cada Assistência Funeral com o Auxílio pai
    // no MESMO grupo (`ASSISTENCIA_AUXILIO_FUNERAL`); sem a isenção o par é
    // exigido pela dependência e proibido pela exclusão ao mesmo tempo.
    // A pareada vem do mesmo campo que `buildCoverageRelations` usa.
    const dependencyParentCode =
      coverage.relationType === COVERAGE_RELATION_DEPENDENT_ON
        ? (coverage.parentCoverageCode ?? coverage.referenceCoverageCode)
        : undefined;

    members.push({
      ref: { kind: 'coverage', coverageId: coverage.id },
      title: coverage.title,
      groupCode: coverage.exclusionGroupCode,
      selected: Boolean(values.coverages[coverage.id]?.enabled),
      key: normalizeCoverageCode(coverage.code),
      ...(dependencyParentCode
        ? { allowedWith: [normalizeCoverageCode(dependencyParentCode)] }
        : {}),
    });
  }

  for (const assistance of catalogs.assistanceCatalog) {
    members.push({
      ref: { kind: 'assistance', code: assistance.code },
      title: assistance.title,
      groupCode: assistance.exclusionGroupCode,
      selected: Boolean(values.assistances[assistance.code]?.enabled),
    });
  }

  // Selects de valor único: o item participante é a opção cujo `value` bate com
  // o código escolhido. As demais opções do select nem entram — não estão
  // selecionadas.
  const funeral = catalogs.assistanceFuneralOptions.find(
    option => option.value === values.assistanceFuneral
  );
  if (funeral) {
    members.push({
      ref: { kind: 'assistanceFuneral' },
      title: funeral.label,
      groupCode: funeral.exclusionGroupCode,
      selected: true,
    });
  }

  const capitalization = catalogs.capitalizationOptions.find(
    option => option.value === values.capitalization
  );
  if (capitalization) {
    members.push({
      ref: { kind: 'capitalization' },
      title: capitalization.label,
      groupCode: capitalization.exclusionGroupCode,
      selected: true,
    });
  }

  return members;
}

/**
 * Conflitos de exclusão mútua para o estado atual do formulário, já roteados
 * para o destino de tela correto. Fonte única consumida pelo view model (tempo
 * real) e pelo schema (bloqueio do submit).
 */
export function findCapitalGlobalExclusionConflicts(
  values: StepCoveragesFormValues,
  catalogs: CapitalGlobalCatalogs
): ExclusionConflictTarget[] {
  const conflicts = findExclusionGroupConflicts(buildMembers(values, catalogs));

  return conflicts.map(conflict => {
    switch (conflict.ref.kind) {
      case 'coverage':
        return {
          kind: 'coverage',
          coverageId: conflict.ref.coverageId,
          message: conflict.message,
        };
      case 'assistance':
        return {
          kind: 'assistance',
          code: conflict.ref.code,
          message: conflict.message,
        };
      case 'assistanceFuneral':
        return { kind: 'assistanceFuneral', message: conflict.message };
      case 'capitalization':
        return { kind: 'capitalization', message: conflict.message };
    }
  });
}
